import { http } from '@/api/http'
import { toListParams, type ListQuery } from '@/api/query'
import type { ListResponse } from '@/types/api'
import type { RequestOptions } from '../crud'

export type ExchangeRateItem = {
  id: string
  currency_id: string
  currency_code: string
  rate_to_base: string
}

/** Immutable once created: a correction is a new version with a later effective_from. */
export type ExchangeRateVersion = {
  id: string
  company_id: string
  effective_from: string
  note: string
  /** manual (typed here) or a «Курсы ЦБ» load: through the server, the browser or a file. */
  source: ExchangeRateSource
  created_by_profile_id: string
  item_count: number
  /** Filled by get and effective; the list returns headers only. */
  items: ExchangeRateItem[]
  created_at: string
}

export type ExchangeRateSource = 'manual' | 'cbu_server' | 'cbu_browser' | 'cbu_file'

/** «Курсы ЦБ»: today in Tashkent, the feed the browser reads, the newest day loaded. */
export type CbuStatus = {
  today: string
  feed_url: string
  last: {
    version_id: string
    rate_date: string
    effective_from: string
    source: ExchangeRateSource
    created_by_profile_id: string
    created_at: string
  } | null
}

/** One «Курсы ЦБ» load; created is false when the day was loaded before. */
export type CbuSyncResult = {
  version: ExchangeRateVersion
  created: boolean
  rate_date: string
}

export type ExchangeRateVersionInput = {
  effective_from: string
  note: string
  /** One rate per active currency; the base currency's rate is 1. */
  items: { currency_id: string; rate_to_base: string }[]
}

const base = '/finance/exchange-rate-versions'

export const exchangeRatesService = {
  path: 'finance/exchange-rate-versions',
  async list(query?: ListQuery, options?: RequestOptions): Promise<ListResponse<ExchangeRateVersion>> {
    const { data } = await http.get<ListResponse<ExchangeRateVersion>>(base, { params: toListParams(query), ...options })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  async get(id: string, options?: RequestOptions) {
    const { data } = await http.get<ExchangeRateVersion>(`${base}/${encodeURIComponent(id)}`, options)
    return data
  },
  async effective(at: string, options?: RequestOptions) {
    const { data } = await http.get<ExchangeRateVersion>(`${base}/effective`, { params: { effective_at: at }, ...options })
    return data
  },
  async create(body: ExchangeRateVersionInput) {
    const { data } = await http.post<ExchangeRateVersion>(base, body)
    return data
  },
  async cbuStatus(options?: RequestOptions) {
    const { data } = await http.get<CbuStatus>(`${base}/cbu`, options)
    return data
  },
  /** The server reads cbu.uz; 503 when it cannot reach it (the droplet's IP is dropped). */
  async cbuSync() {
    const { data } = await http.post<CbuSyncResult>(`${base}/cbu/sync`)
    return data
  },
  /** A feed this browser read from cbu.uz, or a saved cbu.uz JSON file. */
  async cbuImport(feed: unknown, via: 'browser' | 'file') {
    const { data } = await http.post<CbuSyncResult>(`${base}/cbu/import`, { via, feed })
    return data
  },
}
